/**
 * Returns whether the client has contact data for their preferred contact method.
 * While email/mobile are still loading (null), returns true to avoid banner flash.
 *
 * Preferred SMS/WhatsApp requires a mobile number; preferred Email requires an email.
 * When no preferred type is set, either contact method is sufficient.
 */
export const hasUsableContactMethod = ({ email, mobile, preferredContact, preferredContactType } = {}) => {
  // Initial reducer state uses null; avoid treating "not loaded yet" as missing.
  if (email === null && mobile === null) {
    return true;
  }

  const preferred = `${preferredContact || preferredContactType || ''}`.toUpperCase();
  const hasEmail = Boolean(email);
  const hasMobile = Boolean(mobile);

  if (preferred === 'EMAIL') {
    return hasEmail;
  }
  if (preferred === 'SMS' || preferred === 'WHATSAPP') {
    return hasMobile;
  }

  return hasEmail || hasMobile;
};

/**
 * Returns whether the email/mobile currently in messaging state belong to the
 * selected client, so a missing contact method can be distinguished from
 * contact info that has not been fetched yet.
 */
export const hasLoadedContactInfo = ({ contactInfoClientId, clientId } = {}) => {
  if (!clientId || !contactInfoClientId) {
    return false;
  }
  return Number(contactInfoClientId) === Number(clientId);
};

/**
 * Only Clientbook conversations send over SMS/email, so Facebook, Instagram and
 * Google threads never warn about a missing contact method. Opted out clients
 * get the opt-out banner instead.
 */
export const shouldShowNoContactMethodBanner = ({
  conversationType,
  contactInfoLoaded,
  hasContactMethod,
  hasNotOptedOut,
} = {}) => {
  const isClientbookConversation = !conversationType || conversationType === 'C';
  return Boolean(isClientbookConversation && contactInfoLoaded && !hasContactMethod && hasNotOptedOut);
};
